import {
  LOGIN_FIELDS,
  SIGNUP_STEP_FIELDS,
  TOTAL_SIGNUP_STEPS,
  hasFieldErrors,
  resolveLoginErrors,
  resolveSignupStepErrors,
  type CustomerAuthField,
  type CustomerAuthFieldErrorMap,
  type CustomerAuthValues,
} from './validation'
import type { CustomerTwoFactorChallengePayload } from '../../types'

export type LoginInput = { identifier: string; password: string }
export type LoginResult =
  | { kind: 'authenticated' }
  | { kind: 'two_factor_required'; session: CustomerTwoFactorChallengePayload }
  | { kind: 'failed' }
export type SignupInput = {
  email: string
  firstName: string
  lastName: string
  taxCode: string
  phone: string
  residentialAddress: string
  botProtection?: {
    providerId: string
    token: string
  }
}

type SubmitCustomerAuthFormParams = {
  isSignup: boolean
  values: CustomerAuthValues
  nextPath: string
  onClearError: () => void
  clearFieldErrors: () => void
  setSignupStep: (step: number) => void
  markFieldsTouched: (fields: CustomerAuthField[]) => void
  setFieldErrorKeys: (errors: CustomerAuthFieldErrorMap) => void
  onLogin: (input: LoginInput) => Promise<LoginResult>
  onLoginTwoFactorChallenge: (session: CustomerTwoFactorChallengePayload) => void
  onSignup: (input: SignupInput) => Promise<boolean>
  onNavigate: (targetPath: string) => void
  botProtection?: {
    providerId: string
    token: string
  }
}

export type CustomerAuthSubmitResult =
  | 'login_success'
  | 'login_two_factor_required'
  | 'login_error'
  | 'signup_success'
  | 'validation_error'
  | null

export const submitCustomerAuthForm = async ({
  isSignup,
  values,
  nextPath,
  onClearError,
  clearFieldErrors,
  setSignupStep,
  markFieldsTouched,
  setFieldErrorKeys,
  onLogin,
  onLoginTwoFactorChallenge,
  onSignup,
  onNavigate,
  botProtection,
}: SubmitCustomerAuthFormParams): Promise<CustomerAuthSubmitResult> => {
  onClearError()
  clearFieldErrors()

  if (isSignup) {
    for (let step = 1; step <= TOTAL_SIGNUP_STEPS; step += 1) {
      const stepErrors = resolveSignupStepErrors(step, values)
      if (hasFieldErrors(stepErrors)) {
        setSignupStep(step)
        markFieldsTouched(SIGNUP_STEP_FIELDS[step] ?? [])
        setFieldErrorKeys(stepErrors)
        return 'validation_error'
      }
    }

    const phone = `${values.phonePrefix.trim()} ${values.phoneLocalNumber.trim()}`
    const normalizedPostalCode = values.addressPostalCode.replace(/\s+/g, '').trim()
    const residentialAddress = [
      `${values.addressStreet.trim()} ${values.addressStreetNumber.trim()}`,
      `${normalizedPostalCode} ${values.addressCity.trim()} (${values.addressProvince.trim().toUpperCase()})`,
      values.addressCountry.trim(),
    ].join(', ')
    const ok = await onSignup({
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      taxCode: values.taxCode,
      phone,
      residentialAddress,
      botProtection,
    })
    if (ok) {
      return 'signup_success'
    }
    return null
  }

  const loginErrors = resolveLoginErrors(values)
  if (hasFieldErrors(loginErrors)) {
    markFieldsTouched(LOGIN_FIELDS)
    setFieldErrorKeys(loginErrors)
    return 'validation_error'
  }

  const loginResult = await onLogin({
    identifier: values.email,
    password: values.password,
  })
  if (loginResult.kind === 'authenticated') {
    onNavigate(nextPath)
    return 'login_success'
  }
  if (loginResult.kind === 'two_factor_required') {
    onLoginTwoFactorChallenge(loginResult.session)
    return 'login_two_factor_required'
  }
  return 'login_error'
}
